import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import BlogSection from './components/dashboard/BlogSection';
import ProfileAvatar from './components/ProfileAvatar';
import NotificationBell from './components/NotificationBell';
import ApplicationStatusSelector from './components/ApplicationStatusSelector';
import ApplicationStatusBanner from './components/ApplicationStatusBanner';
import ApplicationTrackingModal from './components/ApplicationTrackingModal';
import { API_URL } from './utils/authUtils';
import { nacmeLogo } from './assets/images';

const BACKEND_URL = API_URL;

const StudentDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [studentInfo, setStudentInfo] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invitationsExpanded, setInvitationsExpanded] = useState(true);
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogLoading, setBlogLoading] = useState(true);
  const [blogError, setBlogError] = useState(null);
  const [showBlogSection, setShowBlogSection] = useState(true); // Default to true for graceful degradation
  const [pendingOffers, setPendingOffers] = useState([]);
  const [showMatchesPopup, setShowMatchesPopup] = useState(false);
  const [showStatusBanner, setShowStatusBanner] = useState(false);
  const [statusBannerCompaniesCount, setStatusBannerCompaniesCount] = useState(0);
  const [showApplicationTrackingModal, setShowApplicationTrackingModal] = useState(false);
  const [currentTrackingCompany, setCurrentTrackingCompany] = useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  // Internship matching & resume upload state
  const [internshipMatches, setInternshipMatches] = useState([]);
  const [internshipMatchesTotal, setInternshipMatchesTotal] = useState(0);
  const [internshipLoading, setInternshipLoading] = useState(false);
  const [internshipMatchingEnabled, setInternshipMatchingEnabled] = useState(false);
  const [studentResumes, setStudentResumes] = useState([]);
  const [resumeUploadEnabled, setResumeUploadEnabled] = useState(false);
  const [resumeUploading, setResumeUploading] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);

  // Toggle expanded state for company internship descriptions
  const toggleDescription = (companyId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [companyId]: !prev[companyId]
    }));
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      // Get student info from localStorage
      const storedStudentInfo = localStorage.getItem('studentInfo');
      const authToken = localStorage.getItem('studentAuthToken');
      
      if (!storedStudentInfo || !authToken) {
        // No authentication, redirect to login
        navigate('/student/login');
        return;
      }
      
      try {
        const student = JSON.parse(storedStudentInfo);
        setStudentInfo(student);
        
        // Fetch dashboard data from backend
        const response = await axios.get(`${BACKEND_URL}/api/student/dashboard/${student.id}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        setDashboardData(response.data);

        // Update status banner state
        if (response.data.status_reminder) {
          setShowStatusBanner(response.data.status_reminder.show_banner);
          setStatusBannerCompaniesCount(response.data.status_reminder.companies_count);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        if (error.response?.status === 401) {
          // Token expired or invalid, redirect to login
          localStorage.removeItem('studentInfo');
          localStorage.removeItem('studentAuthToken');
          navigate('/student/login');
        } else {
          setError('Failed to load dashboard data');
          setLoading(false);
        }
      }
    };
    
    fetchDashboardData();
  }, [navigate]);

  // Fetch blog posts and check admin setting
  useEffect(() => {
    const fetchBlogDataWithSettings = async () => {
      try {
        // First, check if blog section should be shown (with graceful fallback)
        let blogEnabled = true; // Default to true
        try {
          const settingResponse = await axios.get(
            `${BACKEND_URL}/api/admin/settings/show_blog_on_student_dashboard`,
            { timeout: 3000 } // Quick timeout
          );
          blogEnabled = settingResponse.data.setting_value === 'true';
        } catch (settingError) {
          console.log('Admin setting check failed, defaulting to show blog:', settingError.message);
          // Continue with blogEnabled = true (graceful degradation)
        }
        
        setShowBlogSection(blogEnabled);
        
        // Only fetch blog posts if enabled
        if (blogEnabled) {
          const response = await axios.get(`${BACKEND_URL}/api/blog/recent?limit=3`);
          setBlogPosts(response.data.posts || []);
        }
      } catch (error) {
        console.error('Error fetching blog data:', error);
        setBlogError('Failed to load blog posts');
        // Even on error, keep blog section visible (graceful degradation)
      } finally {
        setBlogLoading(false);
      }
    };

    const fetchPendingOffers = async () => {
      const authToken = localStorage.getItem('studentAuthToken');
      if (!authToken) return;

      try {
        const response = await axios.get(`${BACKEND_URL}/api/student/offers/pending`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });
        setPendingOffers(response.data.offers || []);
      } catch (error) {
        console.error('Error fetching pending offers:', error);
        // Don't show error for offers - it's not critical
      }
    };

    fetchBlogDataWithSettings();
    fetchPendingOffers();
  }, []);

  // Handle hash navigation to scroll to sections
  useEffect(() => {
    if (location.hash) {
      // Wait for DOM to be ready, then scroll to element
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash, dashboardData]);

  // Fetch internship settings, resumes, and matches
  useEffect(() => {
    const studentData = JSON.parse(localStorage.getItem('studentInfo'));
    const authToken = localStorage.getItem('studentAuthToken');
    if (!studentData?.id || !authToken) return;

    const headers = { headers: { 'Authorization': `Bearer ${authToken}` } };

    // Check feature toggles
    const checkToggles = async () => {
      try {
        const [matchRes, uploadRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/settings/internship_matching_enabled/public`).catch(() => null),
          axios.get(`${BACKEND_URL}/api/admin/settings/student_resume_upload_enabled/public`).catch(() => null),
        ]);
        const matchEnabled = matchRes?.data?.setting_value === 'true';
        const uploadEnabled = uploadRes?.data?.setting_value === 'true';
        setInternshipMatchingEnabled(matchEnabled);
        setResumeUploadEnabled(uploadEnabled);

        // Fetch resumes if upload is enabled
        if (uploadEnabled) {
          try {
            const resumeRes = await axios.get(`${BACKEND_URL}/api/student/resumes/${studentData.id}`, headers);
            setStudentResumes(resumeRes.data || []);
          } catch (e) {
            console.error('Error fetching resumes:', e);
          }
        }

        // Fetch matches if matching is enabled
        if (matchEnabled) {
          setInternshipLoading(true);
          try {
            const matchesRes = await axios.get(`${BACKEND_URL}/api/student/internship-matches/${studentData.id}?page=1&per_page=10`, headers);
            setInternshipMatches(matchesRes.data?.matches || []);
            setInternshipMatchesTotal(matchesRes.data?.total || 0);
          } catch (e) {
            console.error('Error fetching internship matches:', e);
          } finally {
            setInternshipLoading(false);
          }
        }
      } catch (e) {
        // Silent fail — features just won't show
      }
    };
    checkToggles();
  }, []);

  // Resume upload handler
  const handleResumeUpload = async () => {
    if (!resumeFile) return;
    const studentData = JSON.parse(localStorage.getItem('studentInfo'));
    const authToken = localStorage.getItem('studentAuthToken');
    if (!studentData?.id || !authToken) return;

    setResumeUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', resumeFile);
      const res = await axios.post(
        `${BACKEND_URL}/api/student/resumes/${studentData.id}/upload`,
        formData,
        { headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' } }
      );
      setStudentResumes(prev => [res.data, ...prev]);
      setResumeFile(null);
    } catch (e) {
      console.error('Resume upload failed:', e);
      alert(e.response?.data?.detail || 'Failed to upload resume');
    } finally {
      setResumeUploading(false);
    }
  };

  // Set active resume
  const handleSetActiveResume = async (resumeId) => {
    const studentData = JSON.parse(localStorage.getItem('studentInfo'));
    const authToken = localStorage.getItem('studentAuthToken');
    if (!studentData?.id || !authToken) return;

    try {
      await axios.post(
        `${BACKEND_URL}/api/student/resumes/${studentData.id}/set-active/${resumeId}`,
        {},
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      setStudentResumes(prev => prev.map(r => ({
        ...r,
        is_active_for_matching: r.id === resumeId,
      })));
    } catch (e) {
      console.error('Failed to set active resume:', e);
    }
  };

  // Delete resume
  const handleDeleteResume = async (resumeId) => {
    if (!window.confirm('Delete this resume?')) return;
    const studentData = JSON.parse(localStorage.getItem('studentInfo'));
    const authToken = localStorage.getItem('studentAuthToken');
    if (!studentData?.id || !authToken) return;

    try {
      await axios.delete(
        `${BACKEND_URL}/api/student/resumes/${studentData.id}/${resumeId}`,
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      setStudentResumes(prev => prev.filter(r => r.id !== resumeId));
    } catch (e) {
      console.error('Failed to delete resume:', e);
    }
  };

  // Dismiss internship match
  const handleDismissMatch = async (internshipId) => {
    const studentData = JSON.parse(localStorage.getItem('studentInfo'));
    const authToken = localStorage.getItem('studentAuthToken');
    if (!studentData?.id || !authToken) return;

    try {
      await axios.post(
        `${BACKEND_URL}/api/student/internship-matches/${studentData.id}/dismiss/${internshipId}`,
        {},
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      setInternshipMatches(prev => prev.filter(m => m.internship_id !== internshipId));
      setInternshipMatchesTotal(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error('Failed to dismiss match:', e);
    }
  };

  // Record internship click
  const handleInternshipClick = async (match) => {
    const studentData = JSON.parse(localStorage.getItem('studentInfo'));
    const authToken = localStorage.getItem('studentAuthToken');
    if (studentData?.id && authToken) {
      axios.post(
        `${BACKEND_URL}/api/student/internship-matches/${studentData.id}/click/${match.internship_id}`,
        {},
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      ).catch(() => {});
    }
    window.open(match.url, '_blank');
  };

  // Show popup if student has companies they're interested in WITHOUT application status
  useEffect(() => {
    if (dashboardData?.interested_companies?.length > 0) {
      // Check if there are any companies without application status
      const companiesWithoutStatus = dashboardData.interested_companies.filter(
        company => !company.application_status
      );

      // Only show popup if there are companies without status
      if (companiesWithoutStatus.length > 0) {
        // Check if popup was already shown in this session
        const popupShown = sessionStorage.getItem('matchesPopupShown');
        if (!popupShown) {
          setShowMatchesPopup(true);
          sessionStorage.setItem('matchesPopupShown', 'true');
        }
      }
    }
  }, [dashboardData]);

  const handleLogout = () => {
    // Clear authentication data
    localStorage.removeItem('studentInfo');
    localStorage.removeItem('studentAuthToken');

    // Redirect to home page
    navigate('/');
  };

  const refreshDashboard = async () => {
    // Refresh dashboard data after status update
    const storedStudentInfo = localStorage.getItem('studentInfo');
    const authToken = localStorage.getItem('studentAuthToken');

    if (storedStudentInfo && authToken) {
      try {
        const student = JSON.parse(storedStudentInfo);
        const response = await axios.get(`${BACKEND_URL}/api/student/dashboard/${student.id}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        setDashboardData(response.data);

        // Update status banner state
        if (response.data.status_reminder) {
          setShowStatusBanner(response.data.status_reminder.show_banner);
          setStatusBannerCompaniesCount(response.data.status_reminder.companies_count);
        }
      } catch (error) {
        console.error('Error refreshing dashboard:', error);
      }
    }
  };

  const handleDismissBanner = () => {
    // Hide banner immediately in UI
    setShowStatusBanner(false);
  };

  const handleApplyOnlineClick = (e, externalUrl, recruiterId, companyName) => {
    e.preventDefault();

    // Open company application in new tab
    window.open(externalUrl, '_blank');

    // Show tracking modal after brief delay (gives them time to see the new tab)
    setTimeout(() => {
      setCurrentTrackingCompany({ recruiterId, companyName, externalUrl });
      setShowApplicationTrackingModal(true);
    }, 1000);
  };

  const handleCloseTrackingModal = () => {
    setShowApplicationTrackingModal(false);
    setCurrentTrackingCompany(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">{error}</div>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img
                src={nacmeLogo}
                alt="NACME Logo"
                className="h-10 w-auto"
              />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Student Dashboard</h1>
                <p className="text-sm text-gray-500">Welcome back, {dashboardData?.student?.name || studentInfo?.name}!</p>
              </div>
            </div>
            
            {/* Profile section */}
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="hidden md:flex items-center space-x-3">
                <ProfileAvatar
                  name={dashboardData?.student?.name || studentInfo?.name}
                  headshot={dashboardData?.student?.headshot}
                  size="md"
                />
                <div className="hidden lg:block">
                  <p className="text-sm font-medium text-gray-700">
                    {dashboardData?.student?.name || studentInfo?.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {dashboardData?.student?.university || 'Student'}
                  </p>
                </div>
              </div>

              {/* Notification Bell */}
              <NotificationBell />

              <div className="hidden sm:block h-8 w-px bg-gray-300" />

              {/* Your Matches Button - Hidden on mobile */}
              <button
                onClick={() => navigate('/student/dashboard#matches')}
                className="hidden md:block px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors whitespace-nowrap"
              >
                Your Matches
              </button>

              {/* Notifications Button - Hidden on mobile */}
              <button
                onClick={() => navigate('/student/dashboard#notices')}
                className="hidden lg:block px-3 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors whitespace-nowrap"
              >
                Notifications
              </button>

              {/* Update Resume Button - Hidden on mobile, only when feature enabled */}
              {resumeUploadEnabled && (
                <button
                  onClick={() => navigate('/student/dashboard#resumes')}
                  className="hidden md:block px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors whitespace-nowrap"
                >
                  Update Resume
                </button>
              )}

              {/* Companies Button - Hidden on mobile */}
              <button
                onClick={() => navigate('/companies')}
                className="hidden lg:block px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors whitespace-nowrap"
              >
                Companies
              </button>

              {/* Blog Button - Hidden on mobile */}
              <button
                onClick={() => navigate('/blog')}
                className="hidden lg:block px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                Blog
              </button>

              <button
                onClick={handleLogout}
                className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors whitespace-nowrap"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Pending Offers Banner */}
        {pendingOffers.length > 0 && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-3">🎉 You have pending offers!</h3>
            {pendingOffers.map(offer => (
              <div key={offer.id} className="flex justify-between items-center mb-2 last:mb-0">
                <div>
                  <span className="font-medium text-blue-800">
                    {offer.position_title} from {offer.company_name}
                  </span>
                  {offer.compensation && (
                    <span className="text-blue-600 text-sm ml-2">
                      • {offer.compensation}
                    </span>
                  )}
                  {offer.response_deadline && (
                    <span className="text-blue-600 text-sm ml-2">
                      • Respond by {new Date(offer.response_deadline).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <a
                  href={`/offer/view?token=${offer.jwt_token}`}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                >
                  View & Respond
                </a>
              </div>
            ))}
          </div>
        )}

        {/* Application Status Reminder Banner */}
        {showStatusBanner && (
          <ApplicationStatusBanner
            companiesCount={statusBannerCompaniesCount}
            onDismiss={handleDismissBanner}
          />
        )}

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">
              Welcome to Your Dashboard
            </h2>
            {dashboardData?.show_welcome_apply_button && (
              <a
                href={dashboardData.update_link}
                className="inline-block px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors shadow-md"
              >
                Apply to NACME
              </a>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Statistics Cards with Real Data */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Company Interests</h3>
              <p className="text-blue-700">Companies you've expressed interest in</p>
              <div className="mt-4">
                <span className="text-2xl font-bold text-blue-900">
                  {dashboardData?.overview_stats?.companies_interested_in || 0}
                </span>
                <span className="text-sm text-blue-600 ml-2">
                  {dashboardData?.overview_stats?.companies_interested_in === 1 ? 'company' : 'companies'}
                </span>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-green-900 mb-2">Notifications</h3>
              <p className="text-green-700">Messages from recruiters</p>
              <div className="mt-4">
                <span className="text-2xl font-bold text-green-900">
                  {dashboardData?.overview_stats?.unread_notifications || 0}
                </span>
                <span className="text-sm text-green-600 ml-2">unread</span>
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-purple-900 mb-2">Recruiter Interest</h3>
              <p className="text-purple-700">Companies that pinged you</p>
              <div className="mt-4">
                <span className="text-2xl font-bold text-purple-900">
                  {dashboardData?.overview_stats?.recruiter_interactions || 0}
                </span>
                <span className="text-sm text-purple-600 ml-2">
                  {dashboardData?.overview_stats?.recruiter_interactions === 1 ? 'interaction' : 'interactions'}
                </span>
              </div>
            </div>
          </div>

          {/* NACME Process Flow */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-8 border border-blue-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Your Path to Success</h3>
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0 md:space-x-4">
              
              {/* Step 1 */}
              <div className="flex flex-col items-center text-center flex-1">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mb-3">
                  1
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-blue-200 w-full">
                  <h4 className="font-semibold text-blue-900 mb-1">Apply for NACME</h4>
                  <p className="text-sm text-blue-700">Corporate Scholarship</p>
                </div>
              </div>

              {/* Arrow 1 */}
              <div className="hidden md:block text-blue-400 text-2xl">
                →
              </div>
              <div className="md:hidden text-blue-400 text-2xl rotate-90">
                →
              </div>

              {/* Step 2 */}
              <div className="flex flex-col items-center text-center flex-1">
                <div className="w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg mb-3">
                  2
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-green-200 w-full">
                  <h4 className="font-semibold text-green-900 mb-1">Apply with</h4>
                  <p className="text-sm text-green-700">Hiring Companies</p>
                </div>
              </div>

              {/* Arrow 2 */}
              <div className="hidden md:block text-green-400 text-2xl">
                →
              </div>
              <div className="md:hidden text-green-400 text-2xl rotate-90">
                →
              </div>

              {/* Step 3 */}
              <div className="flex flex-col items-center text-center flex-1">
                <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg mb-3">
                  3
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-200 w-full">
                  <h4 className="font-semibold text-purple-900 mb-1">Land an</h4>
                  <p className="text-sm text-purple-700">Internship?</p>
                </div>
              </div>

              {/* Arrow 3 */}
              <div className="hidden md:block text-purple-400 text-2xl">
                →
              </div>
              <div className="md:hidden text-purple-400 text-2xl rotate-90">
                →
              </div>

              {/* Step 4 - Reward */}
              <div className="flex flex-col items-center text-center flex-1">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full flex items-center justify-center font-bold text-lg mb-3">
                  💰
                </div>
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 shadow-sm border border-yellow-200 w-full">
                  <h4 className="font-semibold text-orange-900 mb-1">You may be eligible for a $5K Scholarship</h4>
                  <p className="text-sm text-orange-700">(next fall)</p>
                </div>
              </div>

            </div>
          </div>

          {/* Data Sections */}
          <div className="space-y-6">
            {/* Incomplete Applications - Recruiter Invitations */}
            {dashboardData?.incomplete_applications?.length > 0 && (
              <div className="border-t pt-6">
                <div 
                  className="flex items-center justify-between cursor-pointer mb-4"
                  onClick={() => setInvitationsExpanded(!invitationsExpanded)}
                >
                  <h3 className="text-lg font-semibold text-gray-900">
                    Profile Update Requests ({dashboardData.incomplete_applications.length})
                  </h3>
                  <button className="text-blue-600 hover:text-blue-800">
                    {invitationsExpanded ? '▼ Collapse' : '▶ Expand'}
                  </button>
                </div>
                
                {invitationsExpanded && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="space-y-3">
                      {dashboardData.incomplete_applications.map((invitation, index) => (
                        <div key={index} className="bg-white rounded p-3 border border-blue-100">
                          <p className="text-gray-800 mb-2">
                            <span className="font-semibold text-blue-700">{invitation.recruiter_initials}</span>
                            {' '}requested your NACME profile!
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">{invitation.company_name}</span>
                              <span className="ml-2">• Cycle {invitation.cycle_year}</span>
                            </div>
                            {dashboardData.update_link && (
                              <a
                                href={dashboardData.update_link}
                                className="inline-block px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                              >
                                Update Profile
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {!dashboardData.update_link && (
                        <div className="text-center text-gray-600 text-sm mt-2">
                          <p>No valid application link available at this time.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Recruiter Activity */}
            <div id="notices" className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Recruiter activity ({dashboardData?.recruiter_interactions?.length || 0})
              </h3>
              {dashboardData?.recruiter_interactions?.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.recruiter_interactions.map((interaction, index) => (
                    <div key={index} className="bg-white border rounded-lg p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-semibold text-gray-900 text-lg">{interaction.company_name}</h4>
                            {/* Company Logo */}
                            {interaction.logo && interaction.logo !== 'null' && interaction.logo !== '' && (
                              <img
                                src={interaction.logo.startsWith('/public/') ? interaction.logo.replace('/public/', '/') : interaction.logo}
                                alt={interaction.company_name}
                                className="h-8 w-8 object-contain"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                          </div>
                          <p className="text-gray-600">Recruiter: #{interaction.recruiter_id}</p>
                          <p className="text-sm text-gray-500">Cycle Year: {interaction.cycle_year}</p>
                        </div>
                        <div className="text-right">
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                            interaction.student_applied
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {interaction.student_applied ? 'Applied' : 'Pending Application'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        {interaction.application_date && (
                          <div>
                            <span className="font-medium text-gray-700">NACME Application Date:</span>
                            <p className="text-gray-600">
                              {new Date(interaction.application_date).toLocaleDateString()}
                              {interaction.application_cycle_year && ` (${interaction.application_cycle_year})`}
                            </p>
                          </div>
                        )}
                        <div>
                          <span className="font-medium text-gray-700">Viewed Your Profile On:</span>
                          <p className="text-gray-600">
                            {interaction.shortlisted_at 
                              ? new Date(interaction.shortlisted_at).toLocaleDateString()
                              : '---'
                            }
                          </p>
                        </div>
                        {interaction.apply_invite_sent_at && (
                          <div>
                            <span className="font-medium text-gray-700">Recruiter Notice:</span>
                            <p className="text-gray-600">
                              This recruiter requested your NACME profile
                            </p>
                          </div>
                        )}
                        <div>
                          <span className="font-medium text-gray-700">Company's Application Link:</span>
                          {interaction.external_link_sent_at ? (
                            <div>
                              <p className="text-gray-600 text-sm">
                                Sent: {new Date(interaction.external_link_sent_at).toLocaleDateString()}
                              </p>
                              {interaction.external_application_link && (
                                <a
                                  href={interaction.external_application_link}
                                  onClick={(e) => handleApplyOnlineClick(e, interaction.external_application_link, interaction.recruiter_id, interaction.company_name)}
                                  className="text-blue-600 hover:text-blue-800 text-sm underline cursor-pointer"
                                >
                                  Apply Here
                                </a>
                              )}
                            </div>
                          ) : (
                            <p className="text-gray-600">Pending</p>
                          )}
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Interview Queue:</span>
                          <p className="text-gray-600">
                            {interaction.interview_invite_sent_at
                              ? new Date(interaction.interview_invite_sent_at).toLocaleDateString()
                              : '?'
                            }
                          </p>
                        </div>
                      </div>

                      {/* Application Status Selector */}
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-xs text-gray-600 mb-2 font-medium">Your Application Status:</p>
                        <ApplicationStatusSelector
                          recruiterId={interaction.recruiter_id}
                          companyName={interaction.company_name}
                          currentStatus={interaction.application_status}
                          currentAppliedDate={interaction.student_reported_applied_date}
                          currentOfferDate={interaction.student_reported_offer_date}
                          currentOfferResponseDate={interaction.student_reported_offer_response_date}
                          onUpdate={refreshDashboard}
                        />
                      </div>

                      {/* Notes are private to recruiters and not shown to students */}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-6 text-center">
                  <p className="text-gray-500">No recruiter interactions yet</p>
                  <p className="text-sm text-gray-400 mt-1">Companies will appear here when they express interest in your profile</p>
                </div>
              )}
            </div>

            {/* Company Matches Section */}
            <div id="matches" className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Companies Matching Your Profile ({dashboardData?.matching_companies?.length || 0})
              </h3>
              <p className="text-sm text-blue-600 mb-4">
                Tell us which companies you'd like to work for by clicking 'Interested' below. (Favorited companies will appear in the 'Companies You're Interested In' section.)
                Also, please record your status with each company in 'Your Application Status'. Let us know if you've applied, scheduled an interview, accepted an offer, declined an offer, etc. 
              </p>
              {dashboardData?.matching_companies?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dashboardData.matching_companies.map((company, index) => (
                    <div key={index} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-gray-900 flex-1">{company.organization}</h4>
                        {company.logo && company.logo !== 'null' && company.logo !== '' && (
                          <img
                            src={company.logo.startsWith('/public/') ? company.logo.replace('/public/', '/') : company.logo}
                            alt={company.organization}
                            className="h-8 w-8 object-contain ml-2"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">Min GPA: {company.minimum_gpa || 'N/A'}</p>
                      {company.company_locations && company.company_locations !== 'Not specified' && (
                        <p className="text-sm text-gray-600 mb-2">📍 {company.company_locations}</p>
                      )}
                      {company.internship_description && (
                        <div className="mt-2">
                          <p className={`text-sm text-gray-700 ${expandedDescriptions[company.recruiter_id] ? '' : 'line-clamp-2'}`}>
                            {company.internship_description}
                          </p>
                          {company.internship_description.length > 100 && (
                            <button
                              onClick={() => toggleDescription(company.recruiter_id)}
                              className="text-blue-600 text-xs hover:underline mt-1 font-medium"
                            >
                              {expandedDescriptions[company.recruiter_id] ? 'Show less' : 'Read more'}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Apply Online Link */}
                      {company.external_application_link && company.external_application_link !== 'null' && (
                        <div className="mt-3">
                          <p className="font-medium text-gray-700 text-sm">Application Link:</p>
                          <a
                            href={company.external_application_link}
                            onClick={(e) => handleApplyOnlineClick(e, company.external_application_link, company.recruiter_id, company.organization)}
                            className="text-blue-600 text-sm hover:text-blue-700 underline break-all cursor-pointer"
                          >
                            Apply Online
                          </a>
                        </div>
                      )}

                      {/* Application Status Selector */}
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-gray-600 mb-2 font-medium">Your Application Status:</p>
                        <ApplicationStatusSelector
                          recruiterId={company.recruiter_id}
                          companyName={company.organization}
                          currentStatus={company.application_status}
                          currentAppliedDate={company.student_reported_applied_date}
                          currentOfferDate={company.student_reported_offer_date}
                          currentOfferResponseDate={company.student_reported_offer_response_date}
                          onUpdate={refreshDashboard}
                        />
                      </div>

                      <div className="mt-3 pt-3 border-t flex gap-2">
                        <button
                          onClick={async () => {
                            try {
                              const authToken = localStorage.getItem('studentAuthToken');
                              const studentInfo = JSON.parse(localStorage.getItem('studentInfo'));

                              // Get current interests
                              const currentInterests = dashboardData.interested_companies.map(c => c.recruiter_id);

                              // Add this company to interested list if not already there
                              const updatedInterests = currentInterests.includes(company.recruiter_id)
                                ? currentInterests
                                : [...currentInterests, company.recruiter_id];

                              // Submit to batch endpoint
                              await axios.post(
                                `${BACKEND_URL}/api/applicant-company-interests`,
                                {
                                  applicant_id: studentInfo.id,
                                  interested_recruiter_ids: updatedInterests
                                },
                                { headers: { 'Authorization': `Bearer ${authToken}` } }
                              );

                              // Refresh dashboard data
                              const response = await axios.get(`${BACKEND_URL}/api/student/dashboard/${studentInfo.id}`, {
                                headers: { 'Authorization': `Bearer ${authToken}` }
                              });
                              setDashboardData(response.data);
                            } catch (error) {
                              console.error('Error expressing interest:', error);
                              alert('Failed to express interest. Please try again.');
                            }
                          }}
                          className="flex-1 px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                        >
                          ✓ Interested
                        </button>
                        {/* Not Interested button - Commented out to prevent students from:
                            1. Disappearing from recruiter priority queue
                            2. Changing their mind after recruiter sees interest
                            Students should express interest OR not respond, rather than explicitly declining */}
                        {/* <button
                          onClick={async () => {
                            try {
                              const authToken = localStorage.getItem('studentAuthToken');
                              const studentInfo = JSON.parse(localStorage.getItem('studentInfo'));

                              // Get current interests and remove this company
                              const currentInterests = dashboardData.interested_companies.map(c => c.recruiter_id);
                              const updatedInterests = currentInterests.filter(id => id !== company.recruiter_id);

                              // Submit to batch endpoint
                              await axios.post(
                                `${BACKEND_URL}/api/applicant-company-interests`,
                                {
                                  applicant_id: studentInfo.id,
                                  interested_recruiter_ids: updatedInterests
                                },
                                { headers: { 'Authorization': `Bearer ${authToken}` } }
                              );

                              // Refresh dashboard data
                              const response = await axios.get(`${BACKEND_URL}/api/student/dashboard/${studentInfo.id}`, {
                                headers: { 'Authorization': `Bearer ${authToken}` }
                              });
                              setDashboardData(response.data);
                            } catch (error) {
                              console.error('Error declining interest:', error);
                              alert('Failed to decline interest. Please try again.');
                            }
                          }}
                          className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 transition-colors"
                        >
                          ✗ Not Interested
                        </button> */}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-6 text-center">
                  <p className="text-gray-500">No matching companies found</p>
                  <p className="text-sm text-gray-400 mt-1">Companies matching your profile will appear here</p>
                </div>
              )}
            </div>

            {/* Companies You Expressed Interest In */}
            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Companies You're Interested In ({dashboardData?.interested_companies?.length || 0})
              </h3>
              {dashboardData?.interested_companies?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dashboardData.interested_companies.map((company, index) => (
                    <div key={index} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <h4 className="font-semibold text-gray-900 mb-2">{company.company_name}</h4>
                      <p className="text-sm text-gray-600 mb-2">Recruiter: #{company.recruiter_id}</p>
                      <p className="text-xs text-gray-500 mb-3">
                        Expressed interest: {new Date(company.created_at).toLocaleDateString()}
                      </p>

                      {/* Application Status Selector */}
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-gray-600 mb-2 font-medium">Your Application Status:</p>
                        <ApplicationStatusSelector
                          recruiterId={company.recruiter_id}
                          companyName={company.company_name}
                          currentStatus={company.application_status}
                          currentAppliedDate={company.student_reported_applied_date}
                          currentOfferDate={company.student_reported_offer_date}
                          currentOfferResponseDate={company.student_reported_offer_response_date}
                          onUpdate={refreshDashboard}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-6 text-center">
                  <p className="text-gray-500">No company interests recorded yet</p>
                  <p className="text-sm text-gray-400 mt-1">Start your application to express interest in companies</p>
                </div>
              )}
            </div>

            {/* Resume Upload Section */}
            {resumeUploadEnabled && (
              <div id="resumes" className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Your Resumes
                </h3>
                <p className="text-sm text-green-600 mb-4">
                  Upload your resume to get personalized internship matches. Only PDF files accepted (max 10MB).
                </p>

                {/* Upload area */}
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setResumeFile(e.target.files[0] || null)}
                    className="text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                  />
                  <button
                    onClick={handleResumeUpload}
                    disabled={!resumeFile || resumeUploading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resumeUploading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>

                {/* Resume list */}
                {studentResumes.length > 0 ? (
                  <div className="space-y-2">
                    {studentResumes.map((resume) => (
                      <div key={resume.id} className="flex items-center justify-between bg-white border rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <div>
                            <span className="text-sm font-medium text-gray-900">{resume.filename}</span>
                            <span className="ml-2 text-xs text-gray-400">
                              {resume.uploaded_at ? new Date(resume.uploaded_at).toLocaleDateString() : ''}
                            </span>
                            {resume.is_active_for_matching && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Active for matching
                              </span>
                            )}
                            {resume.processing_status === 'processing' && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Processing...
                              </span>
                            )}
                            {resume.processing_status === 'failed' && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Failed
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!resume.is_active_for_matching && (
                            <button
                              onClick={() => handleSetActiveResume(resume.id)}
                              className="text-xs text-green-600 hover:text-green-800 font-medium"
                            >
                              Use for matching
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteResume(resume.id)}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No resumes uploaded yet.</p>
                )}
              </div>
            )}

            {/* Internship Matches Section */}
            {internshipMatchingEnabled && (
              <div id="internships" className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Internship Opportunities For You
                  {internshipMatchesTotal > 0 && (
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {internshipMatchesTotal}
                    </span>
                  )}
                </h3>

                {internshipLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                  </div>
                ) : internshipMatches.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {internshipMatches.map((match) => (
                      <div key={match.internship_id} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-gray-900 text-sm flex-1 line-clamp-2">{match.title}</h4>
                          {match.organization_logo && (
                            <img
                              src={match.organization_logo}
                              alt={match.organization}
                              className="h-8 w-8 object-contain ml-2 flex-shrink-0"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{match.organization}</p>
                        {match.cities && match.cities.length > 0 && (
                          <p className="text-xs text-gray-400 mb-2">
                            {match.cities.slice(0, 2).join(', ')}
                            {match.remote && ' (Remote)'}
                          </p>
                        )}

                        {/* Match percentage badge */}
                        <div className="mb-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            match.match_percentage >= 80 ? 'bg-green-100 text-green-800' :
                            match.match_percentage >= 60 ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {match.match_percentage}% match
                          </span>
                        </div>

                        {/* Skills pills */}
                        {match.skills && match.skills.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {match.skills.slice(0, 4).map((skill, i) => (
                              <span key={i} className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                {skill}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-between mt-auto pt-2 border-t">
                          <button
                            onClick={() => handleInternshipClick(match)}
                            className="text-sm font-medium text-green-600 hover:text-green-800"
                          >
                            View Listing &rarr;
                          </button>
                          <button
                            onClick={() => handleDismissMatch(match.internship_id)}
                            className="text-xs text-gray-400 hover:text-gray-600"
                          >
                            Not interested
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    {studentResumes.some(r => r.is_active_for_matching) ? (
                      <>
                        <p className="text-gray-500">No matching internships found yet.</p>
                        <p className="text-sm text-gray-400 mt-1">Check back after the next weekly refresh.</p>
                      </>
                    ) : (
                      <>
                        <p className="text-gray-500">Upload a resume to get personalized internship matches</p>
                        <p className="text-sm text-gray-400 mt-1">
                          {resumeUploadEnabled
                            ? 'Use the resume upload section above to get started.'
                            : 'Resume upload will be available soon.'}
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Blog Section - Show only if enabled by admin setting */}
            {showBlogSection && (
              <div className="border-t pt-6 mt-6">
                <BlogSection
                  posts={blogPosts}
                  isLoading={blogLoading}
                  error={blogError}
                />
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Matches Popup Modal */}
      {showMatchesPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
            {/* Close button */}
            <button
              onClick={() => setShowMatchesPopup(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Content */}
            <div className="text-center">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">You have matches!</h3>
              <p className="text-gray-600 mb-6">
                Apply with these companies today!
              </p>
              <button
                onClick={() => {
                  setShowMatchesPopup(false);
                  navigate('/student/dashboard#matches');
                }}
                className="w-full px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
              >
                View Your Matches
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Application Tracking Modal */}
      {showApplicationTrackingModal && currentTrackingCompany && (
        <ApplicationTrackingModal
          recruiterId={currentTrackingCompany.recruiterId}
          companyName={currentTrackingCompany.companyName}
          onClose={handleCloseTrackingModal}
          onStatusUpdate={refreshDashboard}
        />
      )}
    </div>
  );
};

export default StudentDashboard;